import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { isVisibleByDate } from '../../lib/dateVisibility';

/**
 * ============================================================================
 * BannerBlock V2 - Bannière Fixe Txt Centré avec visibilité par dates
 * ============================================================================
 * 
 * DESIGN SYSTEM CONTRACT:
 * ✅ width: 100% du parent (le layout contrôle la largeur)
 * ✅ padding interne configurable
 * ✅ styles internes (background, typography)
 * ❌ INTERDIT: max-width basé sur viewport
 * ❌ INTERDIT: width en % du viewport (ex: 95vw)
 * ❌ INTERDIT: margin auto pour contrôler la largeur
 * 
 * RÈGLE: Le LAYOUT parent définit la largeur, pas le bloc.
 * 
 * VISIBILITÉ PAR DATES (V2):
 * ✅ Supporte start_date / end_date dans content
 * ✅ Format ISO 8601 (UTC)
 * ✅ Fail-safe si dates invalides
 */
const BannerBlock = ({ block }) => {
  const navigate = useNavigate();
  const { 
    title, 
    text, 
    image, 
    uploaded_image, 
    background_color,
    title_color,
    text_color,
    button_text, 
    button_link,
    button_bg_color,
    button_text_color,
    // Champs de visibilité par dates
    start_date,
    end_date
  } = block.content || {};

  // ============================================================
  // VÉRIFICATION VISIBILITÉ PAR DATES (V2)
  // ============================================================
  const isVisible = React.useMemo(() => {
    const visible = isVisibleByDate(start_date, end_date);
    
    if (process.env.NODE_ENV === 'development' && (start_date || end_date)) {
      console.log(`🎯 [BannerBlock] "${title}" - start: ${start_date || 'N/A'}, end: ${end_date || 'N/A'} → visible: ${visible}`);
    }
    
    return visible;
  }, [start_date, end_date, title]);

  // Si non visible par dates, ne rien afficher
  if (!isVisible) {
    return null;
  }

  const bannerImage = image || uploaded_image;

  if (!title && !text) {
    return null;
  }

  const handleButtonClick = () => {
    if (button_link) {
      if (button_link.startsWith('http://') || button_link.startsWith('https://')) {
        window.open(button_link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(button_link);
      }
    }
  };

  const overlayOpacity = bannerImage ? 0.4 : 0;

  return (
    <div 
      className="banner-block"
      data-testid="banner-block"
      data-block-type="banner_fixed_centered"
      style={{
        // ============================================================
        // DESIGN SYSTEM CONTRACT: width = 100% du parent
        // Le layout parent contrôle la largeur, pas ce composant
        // ============================================================
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Container visuel avec background */}
      <div 
        className="relative overflow-hidden rounded-xl"
        style={{
          // Dimensions internes (pas de contrôle de layout)
          width: '100%',
          minHeight: '400px',
          backgroundColor: background_color || (bannerImage ? 'transparent' : '#1a1a2e')
        }}
      >
        {/* Image de fond */}
        {bannerImage && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${encodeURI(decodeURI(bannerImage))})` }}
            data-testid="banner-image"
          >
            <div 
              className="absolute inset-0 bg-black" 
              style={{ opacity: overlayOpacity }}
            />
          </div>
        )}
        
        {/* Contenu centré */}
        <div 
          className="relative flex items-center justify-center text-center"
          style={{
            minHeight: '400px',
            padding: '48px 24px'
          }}
        >
          <div style={{ maxWidth: '800px', width: '100%' }}>
            {title && (
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-4"
                style={{ color: title_color || '#ffffff' }}
                data-testid="banner-title"
              >
                {title}
              </h1>
            )}
            
            {text && (
              <p 
                className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8"
                style={{ color: text_color || 'rgba(255, 255, 255, 0.9)' }}
                data-testid="banner-text"
              >
                {text}
              </p>
            )}
            
            {button_text && (
              <Button 
                size="lg" 
                className={`px-6 sm:px-8 text-sm sm:text-base ${
                  !button_link ? 'cursor-default opacity-90' : 'cursor-pointer'
                }`}
                style={{
                  backgroundColor: button_bg_color || '#ffffff',
                  color: button_text_color || '#000000',
                  border: 'none'
                }}
                onClick={handleButtonClick}
                disabled={!button_link}
                data-testid="banner-button"
              >
                {button_text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerBlock;
